import type { ProliferatorMode } from '../catalog';
import type { BalancePolicy, SolveObjective, SolveStatus } from '../solver';

export type AppLocale = 'zh-CN';
export type DatasetPresetId =
  | 'vanilla'
  | 'vanillaMkIV'
  | 'orbitalring'
  | 'custom';
export type WorkbenchProliferatorPolicyLabel = 'auto' | 'none' | 'speed' | 'productivity';

export const DEFAULT_APP_LOCALE: AppLocale = 'zh-CN';

export interface DatasetPresetText {
  label: string;
  description: string;
}

export interface LocaleBundle {
  workspace: {
    gameConfig: string;
    advancedSettings: string;
    currentConfig: string;
    focus: string;
    viewInput: string;
    resizeResults: string;
    resizeSettings: string;
    resizeHint: string;
  };
  common: {
    auto: string;
    none: string;
    disabled: string;
    yes: string;
    no: string;
    custom: string;
    notSet: string;
    notSolvedYet: string;
    removeSuffix: string;
    closeButton: string;
  };
  enums: {
    objective: Record<SolveObjective, string>;
    balancePolicy: Record<BalancePolicy, string>;
    solveStatus: Record<SolveStatus, string>;
    proliferatorMode: Record<ProliferatorMode, string>;
    workbenchProliferatorPolicy: Record<WorkbenchProliferatorPolicyLabel, string>;
  };
  datasetPresets: Record<DatasetPresetId, DatasetPresetText>;
  page: {
    eyebrow: string;
    title: string;
    heading: string;
    description: string;
  };
  datasetSource: {
    title: string;
    datasetPathPlaceholder: string;
    defaultsPathPlaceholder: string;
    loadButton: string;
    loadingButton: string;
    reloadButton: string;
    autoCacheHint: string;
    clearCacheButton: string;
    loadErrorTitle: string;
    waitingTitle: string;
    waitingDescription: string;
    datasetPathRequired: string;
    loadFailedPrefix: string;
    editorTitle: string;
    editorApplyButton: string;
    editorResetButton: string;
    editorDatasetLabel: string;
    editorDefaultsLabel: string;
    editorHelp: string;
    editorApplyFailedPrefix: string;
    structuredEditorTitle: string;
    structuredEditorHelp: string;
    structuredEditorUnavailable: string;
    structuredEditorTabs: {
      items: string;
      recipes: string;
      buildingRules: string;
      defaults: string;
    };
    structuredEditorAddButton: string;
    structuredEditorRemoveButton: string;
  };
  solveRequest: {
    title: string;
    editTargetsHint: string;
    addTargetTitle: string;
    targetSearchLabel: string;
    targetSearchPlaceholder: string;
    targetPickerEmpty: string;
    selectedTargetLabel: string;
    addTarget: string;
    removeTarget: string;
    objectiveOptions: Record<SolveObjective, string>;
    balancePolicyOptions: Record<BalancePolicy, string>;
    proliferatorPolicyOptions: Record<WorkbenchProliferatorPolicyLabel, string>;
    autoPromoteUnavailableItemsLabel: string;
    rawOverridesLabel: string;
    markAsRaw: string;
    noRawOverrides: string;
    disabledRecipesLabel: string;
    disabledBuildingsLabel: string;
    disableButton: string;
    noDisabledRecipes: string;
    noDisabledBuildings: string;
    recipePreferencesLabel: string;
    addPreference: string;
    recipePreferencesHelp: string;
    noRecipePreferences: string;
    preferredBuildingLabel: string;
    preferredSprayModeLabel: string;
    preferredSprayLevelLabel: string;
    preferredBuildingsLabel: string;
    addPreferredBuilding: string;
    noPreferredBuildings: string;
    preferredBuildingGlobalScope: string;
    advancedOverridesLabel: string;
    advancedOverridesHelp: string;
    buildingParametersLabel: string;
    labStackLayersLabel: string;
    beltSpeedLabel: string;
    startSolveButton: string;
    restartSolveButton: string;
    cancelSolveButton: string;
    autoSolveHint: string;
    autoSolveRunningHint: string;
    autoSolveCancelledHint: string;
    levelPrefix: string;
    validTargetRequired: string;
    invalidAllowedRecipeSelectionMessage: string;
  };
  workbenchConfigs: {
    title: string;
    createDefaultButton: string;
    createDefaultButtonShort: string;
    forkButton: string;
    forkButtonShort: string;
    renameButton: string;
    deleteButton: string;
    renamePlaceholder: string;
    saveRenameButton: string;
    cancelRenameButton: string;
    solveErrorStatus: string;
    noTargets: string;
  };
  summary: {
    catalogTitle: string;
    solveSnapshotTitle: string;
    datasetLabel: string;
    itemsLabel: string;
    recipesLabel: string;
    buildingsLabel: string;
    rawDefaultsLabel: string;
    targetableLabel: string;
    datasetPathLabel: string;
    defaultsPathLabel: string;
    objectiveLabel: string;
    balanceLabel: string;
    sprayLabel: string;
    statusLabel: string;
    targetsLabel: string;
    rawInputsLabel: string;
    rawOverridesLabel: string;
    forcedRecipesLabel: string;
    clearForcedRecipeButton: string;
    disabledRecipesLabel: string;
    disabledBuildingsLabel: string;
    advancedOverridesLabel: string;
    recipePreferencesLabel: string;
    preferredBuildingsLabel: string;
    removePreferredBuildingButton: string;
    proliferatorPreferencesLabel: string;
    noProliferatorPreferences: string;
    loadDatasetToStart: string;
  };
  solveActivity: {
    metricLabel: string;
    runningChipLabel: string;
    initialSolveTitle: string;
    initialSolveDescription: string;
    refreshingTitle: string;
    refreshingDescription: string;
    preparingRequestLabel: string;
    preparingRequestDescription: string;
    syncingCatalogLabel: string;
    syncingCatalogDescription: string;
    loadingSolverLabel: string;
    loadingSolverDescription: string;
    solvingPrimaryLabel: string;
    solvingPrimaryDescription: string;
    solvingRelaxedLabel: string;
    solvingRelaxedDescription: string;
    elapsedLabel: (elapsed: string) => string;
    engineLabel: string;
    pendingResultBadge: string;
    staleResultBadge: string;
    cancelledChipLabel: string;
    cancelledStageLabel: string;
    cancelledStageDescription: (stageLabel: string) => string;
    cancelledInitialTitle: string;
    cancelledInitialDescription: string;
    cancelledRefreshingTitle: string;
    cancelledRefreshingDescription: string;
  };
  snapshot: {
    sectionDescription: {
      targets: string;
      rawInputs: string;
      allowedRecipes: string;
      disabledRecipes: string;
      proliferatorPreferences: string;
      disabledBuildings: string;
      preferredBuildings: string;
      buildingParameters: string;
      dataset: string;
    };
    metricDescription: {
      objective: string;
      balance: string;
      spray: string;
      status: string;
      activity: string;
    };
  };
  recipeStrategy: {
    infeasibleFallback: string;
  };
  overview: {
    summaryTitle: string;
    targetsAndExternalInputsTitle: string;
    buildingsAndPowerTitle: string;
    surplusOutputsTitle: string;
    requestLabel: string;
    actualLabel: string;
    noExternalInputs: string;
    exactLabel: string;
    roundedLabel: string;
    powerLabel: string;
    activeLabel: string;
    roundedPlacementLabel: string;
  };
  recipePlans: {
    title: string;
    inputsLabel: string;
    outputsLabel: string;
    powerLabel: string;
    exactLabel: string;
    roundedLabel: string;
  };
  itemLedger: {
    title: string;
    netInputsTitle: string;
    netOutputsTitle: string;
    intermediatesTitle: string;
    rawBadge: string;
    targetBadge: string;
    surplusBadge: string;
    markRawButton: string;
    unmarkRawButton: string;
    jumpToTopButton: string;
    jumpToBottomButton: string;
    noItems: string;
  };
  diagnostics: {
    title: string;
    diagnosticsLabel: string;
    noDiagnostics: string;
    fallbackTitle: string;
    fallbackDescription: string;
    fallbackNetInputsLabel: string;
    fallbackSurplusLabel: string;
    fallbackApplyButton: string;
    itemBalanceLabel: string;
    producedLabel: string;
    consumedLabel: string;
    netLabel: string;
    copySolveRequestJson: string;
    copySolveRequestJsonDone: string;
    copySolveRequestJsonFailed: string;
    solveRequestJson: string;
    solveResultJson: string;
    audit: {
      overviewTitle: string;
      overviewSummary: (
        itemCount: string,
        recipeCount: string,
        optionCount: string,
        rawInputCount: string,
        solvedRecipeCount: string
      ) => string;
      overviewTimings: (
        total: string,
        graph: string,
        model: string,
        solve: string,
        result: string
      ) => string;
      attemptsTitle: string;
      initialAttemptTitle: (index: number) => string;
      reweightedAttemptTitle: (index: number) => string;
      surplusTypeMilpAttemptTitle: string;
      surplusComplexityMilpAttemptTitle: string;
      complexitySeedAttemptTitle: string;
      complexityAttemptTitle: (round: number) => string;
      linearProgramLabel: string;
      mixedIntegerProgramLabel: string;
      optimalStatusLabel: string;
      infeasibleStatusLabel: string;
      invalidInputStatusLabel: string;
      timedoutStatusLabel: string;
      skippedStatusLabel: string;
      attemptHeading: (context: string, modelKind: string, status: string) => string;
      attemptModelSummary: (
        itemCount: string,
        recipeCount: string,
        optionCount: string,
        constraintCount: string,
        variableCount: string
      ) => string;
      attemptTimings: (build: string, solve: string, total: string) => string;
      attemptSurplus: (itemCount: string, rate: string) => string;
      attemptUsage: (recipeCount: string, optionCount: string) => string;
      attemptObjectiveCost: (objectiveLabel: string, value: string) => string;
      reweightTerminationConverged: string;
      reweightTerminationStagnant: string;
      reweightTerminationMaxRounds: string;
      reweightTerminationDeadline: string;
      reweightTerminationInfeasible: string;
    };
  };
  ready: {
    title: string;
    description: string;
  };
  advancedOverrides: {
    invalidJsonPrefix: string;
    invalidJsonFallback: string;
    mustBeJsonObject: string;
    stringArray: (key: string) => string;
    stringRecord: (key: string) => string;
    numberValue: (key: string) => string;
    numberRecord: (key: string) => string;
    modeValue: (key: string) => string;
    modeRecord: (key: string) => string;
    buildingOverridesInvalid: string;
    buildingOverrideEntryInvalid: (buildingId: string) => string;
    buildingOverrideStackLayersInvalid: (buildingId: string) => string;
    buildingOverrideBeltSpeedInvalid: (buildingId: string) => string;
  };
  flowGraph: {
    title: string;
  };
  focusFilter: {
    description: string;
    fullLineLabel: string;
    placeholder: string;
    clearButton: string;
    focusedSectionTitle: string;
    unfocusedSectionTitle: string;
    addFocusButton: string;
    removeFocusButton: string;
    viewRawPlaceholder: string;
    viewRawDescription: string;
    viewRawBadge: string;
    addViewRawButton: string;
    removeViewRawButton: string;
    clearViewRawButton: string;
    remainingSectionTitle: string;
  };
}

const zhCN: LocaleBundle = {
  workspace: {
    gameConfig: '游戏配置',
    advancedSettings: '数据管理与自定义',
    currentConfig: '当前使用',
    focus: '关注物品',
    viewInput: '淡化上游',
    resizeResults: '调整结果侧栏宽度',
    resizeSettings: '调整设置侧栏宽度',
    resizeHint: '拖动调整栏宽，双击恢复默认布局',
  },
  common: {
    auto: '自动',
    none: '无',
    disabled: '禁用',
    yes: '是',
    no: '否',
    custom: '自定义',
    notSet: '（未设置）',
    notSolvedYet: '尚未求解',
    removeSuffix: '移除',
    closeButton: '关闭',
  },
  enums: {
    objective: {
      min_buildings: '最少占地',
      min_complexity: '复杂度优先',
      min_power: '最低功耗',
      min_weighted: '资源与占地加权',
      min_external_input: '最少外部输入',
    },
    balancePolicy: {
      force_balance: '强制配平',
      allow_surplus: '允许冗余产物',
    },
    solveStatus: {
      optimal: '最优',
      feasible: '可行方案',
      infeasible: '无可行解',
      invalid_input: '输入无效',
    },
    proliferatorMode: {
      none: '无增产剂',
      speed: '加速',
      productivity: '增产',
    },
    workbenchProliferatorPolicy: {
      auto: '自动使用增产剂',
      none: '无增产剂',
      speed: '加速',
      productivity: '增产',
    },
  },
  datasetPresets: {
    vanilla: {
      label: '原版',
      description: '完整的原版兼容数据集，以及配套的默认配置。',
    },
    vanillaMkIV: {
      label: '原版 + 四级增产剂',
      description: '原版兼容数据集，追加 4 级增产剂物品、配方和喷涂等级规则。',
    },
    orbitalring: {
      label: '星环组合',
      description: '星环 1.0.20 + 更多巨构 2.0.1 + 深空来敌 3.4.24 的运行时导出数据快照。',
    },
    custom: {
      label: '自定义路径',
      description: '加载当前 Web 根目录下任意可访问的数据集/默认配置 JSON。',
    },
  },
  page: {
    eyebrow: '求解工作台',
    title: '切换数据集、构造求解请求，并检查浏览器当前展示的精确数值。',
    heading: 'DSP 产线求解工作台',
    description:
      '页面只负责加载数据集、构造求解请求、运行求解器，并渲染展示模型。React 内部不会重复计算隐藏的业务公式。',
  },
  datasetSource: {
    title: '数据集来源',
    datasetPathPlaceholder: './Vanilla.json',
    defaultsPathPlaceholder: './Vanilla.defaults.json',
    loadButton: '加载数据集',
    loadingButton: '加载中...',
    reloadButton: '重新加载数据集',
    autoCacheHint: '当前工作台状态会自动缓存到浏览器。',
    clearCacheButton: '清除所有缓存并重置',
    loadErrorTitle: '数据集加载失败',
    waitingTitle: '等待加载数据集',
    waitingDescription: '先加载一个内置数据集，或者填写自定义数据集文件路径。',
    datasetPathRequired: '必须提供数据集路径。',
    loadFailedPrefix: '加载数据集失败：',
    editorTitle: '内置数据集编辑器',
    editorApplyButton: '应用为当前数据集',
    editorResetButton: '重置为已加载内容',
    editorDatasetLabel: '数据集 JSON',
    editorDefaultsLabel: '默认配置 JSON',
    editorHelp: '用于直接编辑 items / recipes / buildings 相关原始 JSON，并在浏览器内重新解析当前数据集。',
    editorApplyFailedPrefix: '应用编辑失败：',
    structuredEditorTitle: '结构化编辑器',
    structuredEditorHelp:
      '用于直接编辑物品、配方、建筑规则和默认配置。字段修改会同步回上面的 JSON 文本。',
    structuredEditorUnavailable: '当前 JSON 还无法解析，结构化编辑器暂不可用。',
    structuredEditorTabs: {
      items: '物品',
      recipes: '配方',
      buildingRules: '建筑规则',
      defaults: '默认配置',
    },
    structuredEditorAddButton: '新增',
    structuredEditorRemoveButton: '删除',
  },
  solveRequest: {
    title: '求解请求',
    editTargetsHint: '在产线设置中修改目标的需求速度。',
    addTargetTitle: '添加目标',
    targetSearchLabel: '搜索物品',
    targetSearchPlaceholder: '按 ID、名称筛选',
    targetPickerEmpty: '没有匹配的物品。',
    selectedTargetLabel: '已选物品',
    addTarget: '添加目标',
    removeTarget: '移除',
    objectiveOptions: {
      min_buildings: '最少占地',
      min_complexity: '复杂度优先',
      min_power: '最低功耗',
      min_weighted: '资源与占地加权',
      min_external_input: '最少外部输入',
    },
    balancePolicyOptions: {
      force_balance: '强制配平',
      allow_surplus: '允许冗余产物',
    },
    proliferatorPolicyOptions: {
      auto: '自动使用增产剂',
      none: '无增产剂',
      speed: '加速',
      productivity: '增产',
    },
    autoPromoteUnavailableItemsLabel: '不可生产物品自动视为原矿',
    rawOverridesLabel: '原矿输入',
    markAsRaw: '标记为原矿',
    noRawOverrides: '当前没有请求级原矿覆盖。',
    disabledRecipesLabel: '禁用配方',
    disabledBuildingsLabel: '禁用建筑',
    disableButton: '禁用',
    noDisabledRecipes: '当前没有禁用配方。',
    noDisabledBuildings: '当前没有禁用建筑。',
    recipePreferencesLabel: '配方偏好',
    addPreference: '添加偏好',
    recipePreferencesHelp: '这里是按配方的软偏好设置。强制项和低频字段仍然通过高级 JSON 面板输入。',
    noRecipePreferences: '当前没有配方级偏好。',
    preferredBuildingLabel: '优先建筑',
    preferredSprayModeLabel: '优先增产剂模式',
    preferredSprayLevelLabel: '优先增产剂等级',
    preferredBuildingsLabel: '偏好建筑',
    addPreferredBuilding: '添加',
    noPreferredBuildings: '当前没有偏好建筑。',
    preferredBuildingGlobalScope: '全局 (*)',
    advancedOverridesLabel: '高级覆盖 JSON',
    advancedOverridesHelp:
      '用于填写偏好/强制建筑、配方、增产剂等高级请求字段。',
    buildingParametersLabel: '建筑参数',
    labStackLayersLabel: '研究站堆叠层数',
    beltSpeedLabel: '传送带速度 (物品/分)',
    startSolveButton: '立即求解',
    restartSolveButton: '重新求解',
    cancelSolveButton: '中断求解',
    autoSolveHint: '默认保持自动求解。左侧任一会影响求解的输入变更后，右侧结果会自动更新。',
    autoSolveRunningHint: '当前正在自动求解；如长时间没有结果，可以先手动中断。',
    autoSolveCancelledHint: '当前输入的自动求解已中断；点击“重新求解”可继续计算这份请求。',
    levelPrefix: '等级',
    validTargetRequired: '至少需要一个有效目标。',
    invalidAllowedRecipeSelectionMessage: '该允许配方组合会导致当前方案无解，未应用。',
  },
  workbenchConfigs: {
    title: '我的产线',
    createDefaultButton: '新建默认配置',
    createDefaultButtonShort: '新建',
    forkButton: '从当前配置分叉',
    forkButtonShort: '分叉',
    renameButton: '命名',
    deleteButton: '删除配置',
    renamePlaceholder: '输入配置名称',
    saveRenameButton: '保存',
    cancelRenameButton: '取消',
    solveErrorStatus: '求解失败',
    noTargets: '无目标',
  },
  summary: {
    catalogTitle: '数据集概览',
    solveSnapshotTitle: '产线设置',
    datasetLabel: '数据集',
    itemsLabel: '物品',
    recipesLabel: '配方',
    buildingsLabel: '建筑',
    rawDefaultsLabel: '默认原矿',
    targetableLabel: '可作为目标',
    datasetPathLabel: '数据集文件',
    defaultsPathLabel: '默认配置',
    objectiveLabel: '目标函数',
    balanceLabel: '配平策略',
    sprayLabel: '增产剂',
    statusLabel: '求解状态',
    targetsLabel: '目标',
    rawInputsLabel: '原矿',
    rawOverridesLabel: '原矿覆盖',
    forcedRecipesLabel: '允许配方',
    clearForcedRecipeButton: '清除允许配方',
    disabledRecipesLabel: '禁用配方',
    disabledBuildingsLabel: '禁用建筑',
    advancedOverridesLabel: '高级覆盖',
    recipePreferencesLabel: '配方偏好',
    preferredBuildingsLabel: '偏好建筑',
    removePreferredBuildingButton: '移除偏好建筑',
    proliferatorPreferencesLabel: '增产偏好',
    noProliferatorPreferences: '当前没有增产偏好。',
    loadDatasetToStart: '先加载数据集，再开始构造求解请求。',
  },
  solveActivity: {
    metricLabel: '求解活动',
    runningChipLabel: '进行中',
    initialSolveTitle: '正在生成第一版结果',
    initialSolveDescription:
      '浏览器正在计算当前方案，结果返回后会自动显示。',
    refreshingTitle: '正在根据最新设置重新求解',
    refreshingDescription:
      '当前右侧仍显示上一版完成结果，新结果返回后会自动替换。',
    preparingRequestLabel: '整理求解请求',
    preparingRequestDescription:
      '正在根据当前输入整理求解请求，准备把新任务送入求解线程。',
    syncingCatalogLabel: '同步数据集',
    syncingCatalogDescription:
      '正在把当前数据集同步到浏览器内求解线程，避免主界面卡顿。',
    loadingSolverLabel: '准备求解线程',
    loadingSolverDescription:
      '正在准备浏览器内求解线程；首次求解通常会比后续更慢。',
    solvingPrimaryLabel: '求解当前约束',
    solvingPrimaryDescription:
      '正在按照当前目标函数和配平策略搜索可行最优解。',
    solvingRelaxedLabel: '放宽配平后重试',
    solvingRelaxedDescription:
      '强制配平没有得到最优解，正在允许冗余产物后重新求解。',
    elapsedLabel: elapsed => `已用时 ${elapsed}`,
    engineLabel: '浏览器内求解 Worker',
    pendingResultBadge: '等待首个结果',
    staleResultBadge: '显示上一版结果',
    cancelledChipLabel: '已中断',
    cancelledStageLabel: '求解已中断',
    cancelledStageDescription: stageLabel => `已在“${stageLabel}”阶段中断当前求解，可手动重新发起。`,
    cancelledInitialTitle: '当前求解已中断',
    cancelledInitialDescription: '这份输入还没有产出结果，点击重新求解后会继续计算。',
    cancelledRefreshingTitle: '已中断本轮重新求解',
    cancelledRefreshingDescription: '下面仍显示上一版完成结果，点击重新求解可刷新为最新输入。',
  },
  snapshot: {
    sectionDescription: {
      targets:
        '目标约束：每个条目定义一个物品的净输出速率下界，单位为个/分。求解器仅接受同时满足全部目标条目的可行方案。',
      rawInputs:
        '原矿约束：列表中的物品被视为原始输入，求解器不会尝试合成它们。默认原矿由数据集定义，可在此处添加或移除。',
      allowedRecipes:
        '允许配方约束：对某个输出物品，仅允许列表中的配方承担该物品的供给。未列出的同类产出配方会从可行域中排除。',
      disabledRecipes:
        '禁用配方约束：列表中的配方会被全局移出可行域。求解器在任何生产链路中都不得实例化这些配方。',
      proliferatorPreferences:
        '增产偏好约束：定义全局或配方级的增产剂模式与等级请求。配方级条目优先于全局条目，并直接影响求解请求中的增产策略选择。',
      disabledBuildings:
        '禁用建筑约束：列表中的建筑会被全局移出候选建筑集合。求解器不得将任何配方分配到这些建筑上。',
      preferredBuildings:
        '偏好建筑约束：定义全局或配方级的建筑选择偏好。配方级条目优先于全局条目，并用于约束或引导对应配方的建筑分配。',
      buildingParameters:
        '建筑参数：覆盖建筑的运行参数。研究站堆叠层数影响占地面积计算，传送带速度影响分馏器吞吐。',
      dataset:
        '数据集配置：选择或自定义游戏数据集，包括物品、配方和建筑定义。更改数据集后需要重新加载。',
    },
    metricDescription: {
      objective:
        '目标函数：定义求解器在满足全部约束后所最小化的主目标，例如占地、功耗或外部输入。',
      balance:
        '配平策略：定义中间产物流的守恒约束形式。强制配平要求内部流量严格守恒；允许盈余则允许产生未被继续消费的剩余输出。',
      spray:
        '增产剂策略：定义全局增产模式与等级的默认请求。若存在配方级偏好或强制覆盖，则以更具体的条目为准。',
      status:
        '求解状态：描述当前请求是否找到可行最优解，以及该状态是否满足全部约束与目标函数要求。',
      activity:
        '求解活动：表示浏览器是否正在为最新输入重新求解。进行中时，右侧结果可能仍是上一版完成结果。',
    },
  },
  recipeStrategy: {
    infeasibleFallback: '该修改会导致当前方案无解，已撤销',
  },
  overview: {
    summaryTitle: '方案总览',
    targetsAndExternalInputsTitle: '目标与外部输入',
    buildingsAndPowerTitle: '建筑与功耗',
    surplusOutputsTitle: '冗余产物',
    requestLabel: '需求',
    actualLabel: '实际',
    noExternalInputs: '没有外部输入。',
    exactLabel: '精确',
    roundedLabel: '放置',
    powerLabel: '功耗',
    activeLabel: '工作功耗',
    roundedPlacementLabel: '按工作/待机负载加权',
  },
  recipePlans: {
    title: '配方方案',
    inputsLabel: '输入',
    outputsLabel: '输出',
    powerLabel: '功耗',
    exactLabel: '精确',
    roundedLabel: '取整',
  },
  itemLedger: {
    title: '全物品总表',
    netInputsTitle: '净输入',
    netOutputsTitle: '净输出',
    intermediatesTitle: '中间流转',
    rawBadge: '原矿',
    targetBadge: '目标',
    surplusBadge: '冗余',
    markRawButton: '标记为原矿',
    unmarkRawButton: '取消原矿',
    jumpToTopButton: '回到顶部',
    jumpToBottomButton: '滚动到底部',
    noItems: '当前分组没有物品。',
  },
  diagnostics: {
    title: '诊断与审计',
    diagnosticsLabel: '诊断信息',
    noDiagnostics: '没有诊断信息。',
    fallbackTitle: '强制配平无解，但允许冗余时存在可行方案',
    fallbackDescription: '下面这组结果来自 allow_surplus，可作为定位副产物或原矿设置的提示。',
    fallbackNetInputsLabel: 'Fallback 净输入',
    fallbackSurplusLabel: 'Fallback 冗余产物',
    fallbackApplyButton: '切换为允许冗余',
    itemBalanceLabel: '物品平衡',
    producedLabel: '生成',
    consumedLabel: '消耗',
    netLabel: '净值',
    copySolveRequestJson: '复制 JSON',
    copySolveRequestJsonDone: 'JSON 已复制',
    copySolveRequestJsonFailed: '复制失败',
    solveRequestJson: '求解请求 JSON',
    solveResultJson: '求解结果 JSON',
    audit: {
      overviewTitle: '本次求解概览',
      overviewSummary: (itemCount, recipeCount, optionCount, rawInputCount, solvedRecipeCount) =>
        `图裁剪后保留 ${itemCount} 个物品、${recipeCount} 个配方、${optionCount} 个候选方案，原矿输入 ${rawInputCount} 种，本次解实际使用 ${solvedRecipeCount} 个配方。`,
      overviewTimings: (total, graph, model, solve, result) =>
        `总耗时 ${total}，其中建图 ${graph}，建模 ${model}，求解 ${solve}，结果整理 ${result}。`,
      attemptsTitle: '实际求解过程',
      initialAttemptTitle: index => `第 ${index} 次求解`,
      reweightedAttemptTitle: index =>
        `第 ${index} 次求解（按上一轮冗余结果重新加权）`,
      surplusTypeMilpAttemptTitle: '冗余种类最小化（混合整数规划）',
      surplusComplexityMilpAttemptTitle: '固定冗余后的复杂度优化（混合整数规划）',
      complexitySeedAttemptTitle: '复杂度预求解',
      complexityAttemptTitle: round => `复杂度主求解第 ${round} 轮`,
      linearProgramLabel: '线性规划',
      mixedIntegerProgramLabel: '混合整数规划',
      optimalStatusLabel: '已找到最优解',
      infeasibleStatusLabel: '无可行解',
      invalidInputStatusLabel: '输入无效',
      timedoutStatusLabel: '超时（近似解）',
      skippedStatusLabel: '已跳过',
      attemptHeading: (context, modelKind, status) =>
        `${context}：${modelKind}，${status}。`,
      attemptModelSummary: (
        itemCount,
        recipeCount,
        optionCount,
        constraintCount,
        variableCount
      ) =>
        `本轮模型包含 ${itemCount} 个物品平衡条件、${recipeCount} 个配方、${optionCount} 个候选方案、${constraintCount} 条约束、${variableCount} 个变量。`,
      attemptTimings: (build, solve, total) =>
        `本轮耗时：建模 ${build}，求解 ${solve}，小计 ${total}。`,
      attemptSurplus: (itemCount, rate) =>
        `本轮冗余：${itemCount} 种，合计 ${rate} / 分。`,
      attemptUsage: (recipeCount, optionCount) =>
        `本轮实际使用：${recipeCount} 个配方，${optionCount} 条产线。`,
      attemptObjectiveCost: (objectiveLabel, value) =>
        `本轮${objectiveLabel}代价 ${value}。`,
      reweightTerminationConverged: '冗余产物已收敛至 1 种以下，无需继续加权。',
      reweightTerminationStagnant: '连续多轮结果未变化，停止迭代。',
      reweightTerminationMaxRounds: '已达到最大迭代轮数。',
      reweightTerminationDeadline: '已超出时间预算。',
      reweightTerminationInfeasible: '加权后求解未能找到可行解。',
    },
  },
  ready: {
    title: '准备就绪',
    description: '数据集已加载。调整左侧请求后，页面会自动重新求解并更新结果。',
  },
  advancedOverrides: {
    invalidJsonPrefix: 'JSON 无效：',
    invalidJsonFallback: 'JSON 无效。',
    mustBeJsonObject: '高级覆盖必须是一个 JSON 对象。',
    stringArray: key => `${key} 在提供时必须是字符串数组。`,
    stringRecord: key => `${key} 在提供时必须是值为字符串的对象。`,
    numberValue: key => `${key} 在提供时必须是有限数字。`,
    numberRecord: key => `${key} 在提供时必须是值为有限数字的对象。`,
    modeValue: key => `${key} 在提供时必须是 none、speed 或 productivity。`,
    modeRecord: key => `${key} 在提供时必须是值为 none、speed 或 productivity 的对象。`,
    buildingOverridesInvalid: 'buildingOverrides 必须是以建筑 ID 为键的对象。',
    buildingOverrideEntryInvalid: buildingId => `buildingOverrides["${buildingId}"] 必须是一个对象。`,
    buildingOverrideStackLayersInvalid: buildingId => `buildingOverrides["${buildingId}"].stackLayers 必须是正整数。`,
    buildingOverrideBeltSpeedInvalid: buildingId => `buildingOverrides["${buildingId}"].beltSpeedItemsPerMin 必须是正数。`,
  },
  flowGraph: {
    title: '物品流图',
  },
  focusFilter: {
    description: '选择当前产线中的物品，查看其上游子图（含副产物）。多个物品取并集；用量保留当前产线数值，范围外内容淡化。',
    fullLineLabel: '全产线',
    placeholder: '关注物品…',
    clearButton: '清除关注',
    focusedSectionTitle: '关注范围',
    unfocusedSectionTitle: '范围外',
    addFocusButton: '关注',
    removeFocusButton: '取消关注',
    viewRawPlaceholder: '淡化上游…',
    viewRawDescription: '将物品视作外供，淡化专用上游；其他需求共用的配方仍保留。仅改变视图，不修改原矿或重新求解。',
    viewRawBadge: '视图外供',
    addViewRawButton: '淡化上游',
    removeViewRawButton: '恢复上游',
    clearViewRawButton: '恢复全部上游',
    remainingSectionTitle: '视图范围',
  },
};

export function getLocaleBundle(locale: AppLocale = DEFAULT_APP_LOCALE): LocaleBundle {
  if (locale === 'zh-CN') {
    return zhCN;
  }

  return zhCN;
}

export function getDatasetPresetText(
  presetId: DatasetPresetId,
  locale: AppLocale = DEFAULT_APP_LOCALE
): DatasetPresetText {
  return getLocaleBundle(locale).datasetPresets[presetId];
}

function formatDecimal(
  value: number,
  locale: AppLocale,
  minimumFractionDigits: number,
  maximumFractionDigits: number
): string {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(value);
}

export function formatRate(
  ratePerMin: number,
  locale: AppLocale = DEFAULT_APP_LOCALE
): string {
  return `${formatDecimal(ratePerMin, locale, 2, 2)} / 分`;
}

export function formatPower(
  value: number,
  locale: AppLocale = DEFAULT_APP_LOCALE
): string {
  return `${formatDecimal(value, locale, 2, 2)} MW`;
}

export function formatSolveObjective(
  objective: SolveObjective,
  locale: AppLocale = DEFAULT_APP_LOCALE
): string {
  return getLocaleBundle(locale).enums.objective[objective];
}

export function formatBalancePolicy(
  policy: BalancePolicy,
  locale: AppLocale = DEFAULT_APP_LOCALE
): string {
  return getLocaleBundle(locale).enums.balancePolicy[policy];
}

export function formatSolveStatus(
  status: SolveStatus | null | undefined,
  locale: AppLocale = DEFAULT_APP_LOCALE
): string {
  if (!status) {
    return getLocaleBundle(locale).common.notSolvedYet;
  }

  return getLocaleBundle(locale).enums.solveStatus[status];
}

export function formatProliferatorMode(
  mode: ProliferatorMode,
  locale: AppLocale = DEFAULT_APP_LOCALE
): string {
  return getLocaleBundle(locale).enums.proliferatorMode[mode];
}

export function formatWorkbenchProliferatorPolicy(
  policy: WorkbenchProliferatorPolicyLabel,
  locale: AppLocale = DEFAULT_APP_LOCALE
): string {
  return getLocaleBundle(locale).enums.workbenchProliferatorPolicy[policy];
}

export function formatProliferatorLabel(
  proliferatorMode: ProliferatorMode,
  proliferatorLevel: number,
  locale: AppLocale = DEFAULT_APP_LOCALE
): string {
  const bundle = getLocaleBundle(locale);

  if (proliferatorMode === 'none' || proliferatorLevel === 0) {
    return bundle.enums.proliferatorMode.none;
  }

  return `${bundle.enums.proliferatorMode[proliferatorMode]} ${bundle.solveRequest.levelPrefix} ${proliferatorLevel}`;
}

export function formatPreferredProliferatorLabel(
  proliferatorMode: ProliferatorMode | undefined,
  proliferatorLevel: number | undefined,
  locale: AppLocale = DEFAULT_APP_LOCALE
): string | undefined {
  const bundle = getLocaleBundle(locale);

  if (proliferatorMode === undefined && proliferatorLevel === undefined) {
    return undefined;
  }

  if (proliferatorMode === 'none') {
    return bundle.enums.proliferatorMode.none;
  }

  if (proliferatorMode !== undefined && proliferatorLevel !== undefined) {
    return `${bundle.enums.proliferatorMode[proliferatorMode]} ${bundle.solveRequest.levelPrefix} ${proliferatorLevel}`;
  }

  if (proliferatorMode !== undefined) {
    return `${bundle.enums.proliferatorMode[proliferatorMode]}（${bundle.common.auto}等级）`;
  }

  if (proliferatorLevel !== undefined) {
    return `${bundle.common.auto}模式 ${bundle.solveRequest.levelPrefix} ${proliferatorLevel}`;
  }

  return undefined;
}
